import React, { useState, useRef, useMemo } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
  arrayMove,
  useSortable,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import { useShowStore } from '../../stores/showStore';
import { cuesAPI } from '../../lib/api';
import { useCan } from '../../hooks/useCan';
import { parseShowStartDate } from '../../lib/showTime';
import { computeInTimes } from '../../lib/rundownDerive';
import { RUNDOWN_KEYBOARD_CODES, noMouseFocus } from '../rundown/dragHandle';

// RECORD_STREAM, NOTE and BREAK are retired and no longer offered (see
// CUE_TYPE_ACCENT in CueRow); the backend still accepts them so existing rows
// are unaffected. SECTION_BREAK is a different type and stays.
const CUE_TYPES = [
  'SEGMENT', 'CAMERA', 'REPLAY', 'GRAPHIC', 'AUDIO',
  'VT_VOX', 'VT', 'PRESENTERS_TO_CAMERA', 'SHOW_START', 'SECTION_BREAK', 'GROUP', 'PRE_SHOW',
];

// Editable column definitions — order determines left-to-right paste fill
const COLUMNS = [
  { field: 'label',             header: 'Label',     type: 'text',     width: '200px' },
  { field: 'planned_duration',  header: 'Duration',  type: 'duration', width: '76px'  },
  { field: 'cue_type',          header: 'Type',      type: 'select',   width: '148px' },
  { field: 'script',            header: 'Script',    type: 'text',     width: '220px' },
  { field: 'notes',             header: 'Notes',     type: 'text',     width: '140px' },
  { field: 'top_right_logo',    header: 'TR Logo',   type: 'text',     width: '120px' },
  { field: 'bottom_right_logo', header: 'BR Logo',   type: 'text',     width: '120px' },
  { field: 'other_graphics',    header: 'Other GFX', type: 'text',     width: '140px' },
];

const PASTE_FIELDS = COLUMNS.map(c => c.field);

// What a guest without production access may edit — mirrors TEXT_FIELDS in
// backend/src/lib/access.js, which is what actually enforces it.
const TEXT_FIELDS = ['script', 'notes', 'top_right_logo', 'bottom_right_logo', 'other_graphics'];

// ─── Helpers ───────────────────────────────────────────────────────────────────

function formatDuration(seconds) {
  if (seconds == null || seconds === '') return '';
  const s = Number(seconds);
  if (isNaN(s)) return '';
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

function parseDuration(str) {
  if (!str && str !== 0) return null;
  str = String(str).trim();
  if (str.includes(':')) {
    const parts = str.split(':').map(n => parseInt(n) || 0);
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
    if (parts.length === 2) return parts[0] * 60 + parts[1];
  }
  const n = parseInt(str);
  return isNaN(n) ? null : n;
}

function formatClockTime(date) {
  if (!date) return '—';
  const h = String(date.getHours()).padStart(2, '0');
  const m = String(date.getMinutes()).padStart(2, '0');
  const s = String(date.getSeconds()).padStart(2, '0');
  return `${h}:${m}:${s}`;
}

function processValueForField(field, rawValue) {
  const v = String(rawValue ?? '').trim();
  if (field === 'planned_duration') {
    if (v === '') return undefined;         // empty duration cell → skip
    const parsed = parseDuration(v);
    return parsed !== null ? parsed : undefined;
  }
  if (field === 'cue_type') {
    if (v === '') return undefined;         // can't blank an enum — skip
    const upper = v.toUpperCase();
    return CUE_TYPES.includes(upper) ? upper : undefined;
  }
  // text fields: empty cell → '-' so the paste is visible and the row position is preserved
  return v === '' ? '-' : v;
}

// ─── Sortable row ──────────────────────────────────────────────────────────────

function SortableRow({
  cue,
  cueIndex,
  showId,
  selected,
  onSelect,
  focusedCell,
  onCellFocus,
  onCellBlur,
  onCellChange,
  onPaste,
  onDelete,
  timeIn,
  timeOut,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: cue.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  const statusColor =
    cue.status === 'on_air'   ? 'bg-red-900/30 border-l-2 border-l-red-500' :
    cue.status === 'complete' ? 'opacity-40' : '';

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`group border-b border-app-border ${statusColor} ${selected ? 'bg-blue-900/20' : 'hover:bg-app-panel'}`}
    >
      {/* Drag handle + row number */}
      <td className="px-1 select-none border-r border-app-border" style={{ width: '52px' }}>
        <div className="flex items-center gap-1">
          <span
            {...attributes}
            {...listeners}
            {...noMouseFocus}
            className="cursor-grab text-gray-700 hover:text-gray-500 px-0.5"
            title="Drag to reorder"
          >
            ⋮⋮
          </span>
          <span className="text-gray-600 text-xs font-mono">{cueIndex + 1}</span>
        </div>
      </td>

      {/* Select checkbox */}
      <td className="px-1 border-r border-app-border" style={{ width: '32px' }}>
        <input
          type="checkbox"
          checked={selected}
          onChange={e => onSelect(cue.id, e.target.checked)}
          className="accent-blue-500 cursor-pointer"
        />
      </td>

      {/* Label */}
      <td className="border-r border-app-border p-0" style={{ width: '200px' }}>
        <Cell
          col={COLUMNS[0]}
          cue={cue}
          isFocused={focusedCell?.cueId === cue.id && focusedCell?.field === 'label'}
          onFocus={() => onCellFocus(cue.id, 'label')}
          onBlur={() => onCellBlur(cue.id, 'label')}
          onChange={val => onCellChange(cue.id, 'label', val)}
          onPaste={e => onPaste(e, cueIndex, 'label')}
        />
      </td>

      {/* Duration */}
      <td className="border-r border-app-border p-0" style={{ width: '76px' }}>
        <Cell
          col={COLUMNS[1]}
          cue={cue}
          isFocused={focusedCell?.cueId === cue.id && focusedCell?.field === 'planned_duration'}
          onFocus={() => onCellFocus(cue.id, 'planned_duration')}
          onBlur={() => onCellBlur(cue.id, 'planned_duration')}
          onChange={val => onCellChange(cue.id, 'planned_duration', val)}
          onPaste={e => onPaste(e, cueIndex, 'planned_duration')}
        />
      </td>

      {/* Time In — read-only */}
      <td className="border-r border-app-border px-2 py-1.5" style={{ width: '76px' }}>
        <span className="text-xs font-mono text-gray-500">{formatClockTime(timeIn)}</span>
      </td>

      {/* Time Out — read-only */}
      <td className="border-r border-app-border px-2 py-1.5" style={{ width: '76px' }}>
        <span className="text-xs font-mono text-gray-500">{formatClockTime(timeOut)}</span>
      </td>

      {/* Type */}
      <td className="border-r border-app-border p-0" style={{ width: '148px' }}>
        <Cell
          col={COLUMNS[2]}
          cue={cue}
          isFocused={focusedCell?.cueId === cue.id && focusedCell?.field === 'cue_type'}
          onFocus={() => onCellFocus(cue.id, 'cue_type')}
          onBlur={() => onCellBlur(cue.id, 'cue_type')}
          onChange={val => onCellChange(cue.id, 'cue_type', val)}
          onPaste={e => onPaste(e, cueIndex, 'cue_type')}
        />
      </td>

      {/* Script */}
      <td className="border-r border-app-border p-0" style={{ width: '220px' }}>
        <Cell
          col={COLUMNS[3]}
          cue={cue}
          isFocused={focusedCell?.cueId === cue.id && focusedCell?.field === 'script'}
          onFocus={() => onCellFocus(cue.id, 'script')}
          onBlur={() => onCellBlur(cue.id, 'script')}
          onChange={val => onCellChange(cue.id, 'script', val)}
          onPaste={e => onPaste(e, cueIndex, 'script')}
        />
      </td>

      {/* Notes */}
      <td className="border-r border-app-border p-0" style={{ width: '140px' }}>
        <Cell
          col={COLUMNS[4]}
          cue={cue}
          isFocused={focusedCell?.cueId === cue.id && focusedCell?.field === 'notes'}
          onFocus={() => onCellFocus(cue.id, 'notes')}
          onBlur={() => onCellBlur(cue.id, 'notes')}
          onChange={val => onCellChange(cue.id, 'notes', val)}
          onPaste={e => onPaste(e, cueIndex, 'notes')}
        />
      </td>

      {/* TR Logo */}
      <td className="border-r border-app-border p-0" style={{ width: '120px' }}>
        <Cell
          col={COLUMNS[5]}
          cue={cue}
          isFocused={focusedCell?.cueId === cue.id && focusedCell?.field === 'top_right_logo'}
          onFocus={() => onCellFocus(cue.id, 'top_right_logo')}
          onBlur={() => onCellBlur(cue.id, 'top_right_logo')}
          onChange={val => onCellChange(cue.id, 'top_right_logo', val)}
          onPaste={e => onPaste(e, cueIndex, 'top_right_logo')}
        />
      </td>

      {/* BR Logo */}
      <td className="border-r border-app-border p-0" style={{ width: '120px' }}>
        <Cell
          col={COLUMNS[6]}
          cue={cue}
          isFocused={focusedCell?.cueId === cue.id && focusedCell?.field === 'bottom_right_logo'}
          onFocus={() => onCellFocus(cue.id, 'bottom_right_logo')}
          onBlur={() => onCellBlur(cue.id, 'bottom_right_logo')}
          onChange={val => onCellChange(cue.id, 'bottom_right_logo', val)}
          onPaste={e => onPaste(e, cueIndex, 'bottom_right_logo')}
        />
      </td>

      {/* Other GFX */}
      <td className="border-r border-app-border p-0" style={{ width: '140px' }}>
        <Cell
          col={COLUMNS[7]}
          cue={cue}
          isFocused={focusedCell?.cueId === cue.id && focusedCell?.field === 'other_graphics'}
          onFocus={() => onCellFocus(cue.id, 'other_graphics')}
          onBlur={() => onCellBlur(cue.id, 'other_graphics')}
          onChange={val => onCellChange(cue.id, 'other_graphics', val)}
          onPaste={e => onPaste(e, cueIndex, 'other_graphics')}
        />
      </td>

      {/* Delete button */}
      <td className="px-1" style={{ width: '32px' }}>
        <button
          onClick={() => onDelete(cue.id)}
          className="text-gray-700 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs px-1"
          title="Delete row"
        >
          ✕
        </button>
      </td>
    </tr>
  );
}

// ─── Individual editable cell ──────────────────────────────────────────────────

function Cell({ col, cue, isFocused, onFocus, onBlur, onChange, onPaste }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);

  function startEdit() {
    const current =
      col.type === 'duration' ? formatDuration(cue[col.field]) : (cue[col.field] ?? '');
    setDraft(current);
    setEditing(true);
    onFocus();
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  function commitEdit() {
    setEditing(false);
    onBlur();
    const trimmed = draft.trim();
    if (col.type === 'duration') {
      const parsed = parseDuration(trimmed);
      if (parsed !== null && parsed !== cue[col.field]) onChange(parsed);
    } else {
      if (trimmed !== (cue[col.field] ?? '')) onChange(trimmed);
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') { e.preventDefault(); commitEdit(); }
    if (e.key === 'Escape') { setEditing(false); onBlur(); }
  }

  const displayValue =
    col.type === 'duration' ? formatDuration(cue[col.field]) : (cue[col.field] ?? '');

  if (col.type === 'select') {
    return (
      <select
        value={cue[col.field] ?? ''}
        onChange={e => onChange(e.target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
        onPaste={onPaste}
        className="w-full h-full bg-transparent text-gray-300 text-xs px-2 py-1.5 outline-none focus:bg-[rgb(var(--c-input-green-focus))] cursor-pointer appearance-none"
      >
        {CUE_TYPES.map(t => (
          <option key={t} value={t} className="bg-app-header text-gray-300">{t}</option>
        ))}
      </select>
    );
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commitEdit}
        onKeyDown={handleKeyDown}
        onPaste={onPaste}
        className="w-full bg-[rgb(var(--c-input-green))] text-app-fg text-xs px-2 py-1.5 outline-none border border-green-700/50 font-mono"
        style={{ minWidth: 0 }}
      />
    );
  }

  return (
    <div
      onClick={startEdit}
      onFocus={startEdit}
      onPaste={onPaste}
      tabIndex={0}
      className="w-full text-xs px-2 py-1.5 text-gray-400 cursor-text truncate outline-none focus:bg-[rgb(var(--c-input-green))] focus:text-app-fg min-h-[28px]"
    >
      {displayValue || <span className="text-gray-700">—</span>}
    </div>
  );
}

// ─── Main RawView component ────────────────────────────────────────────────────

export default function RawView({ showId, show }) {
  const cues = useShowStore(s => s.cues);
  const { setCues, updateCue, addCue, removeCue, reorderCues } = useShowStore.getState();

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [focusedCell, setFocusedCell] = useState(null);
  const canProduce = useCan('production:edit');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates, keyboardCodes: RUNDOWN_KEYBOARD_CODES }),
  );

  // ── Time calculations (same logic as RundownTable) ───────────────────────────
  const showStartDate = useMemo(
    () => parseShowStartDate(show),
    [show?.show_start_time, show?.scheduled_start]
  );

  const inTimes = useMemo(() => computeInTimes(cues, show), [cues, showStartDate]);

  // ── Cell update ──────────────────────────────────────────────────────────────
  async function handleCellChange(cueId, field, value) {
    // A text-only guest's edit to any other column is dropped, and the cell
    // falls back to the stored value.
    if (!canProduce && !TEXT_FIELDS.includes(field)) return;
    const updates = { [field]: value };
    updateCue({ id: cueId, ...updates });
    try {
      await cuesAPI.update(showId, cueId, updates);
    } catch (err) {
      console.error('Cell update failed:', err.message);
    }
  }

  // ── Paste from spreadsheet ───────────────────────────────────────────────────
  function handlePaste(e, startCueIndex, startField) {
    const text = e.clipboardData?.getData('text/plain') ?? '';
    if (!text) return;

    const rows = text.split(/\r?\n/);
    // Trim the single trailing newline that Excel/Sheets always appends
    while (rows.length > 0 && rows[rows.length - 1] === '') rows.pop();

    // Single-cell paste with no tabs — let browser handle naturally
    if (rows.length === 1 && !rows[0].includes('\t')) return;

    e.preventDefault();

    const startColIdx = PASTE_FIELDS.indexOf(startField);
    if (startColIdx === -1) return;

    const snapshot = useShowStore.getState().cues;

    rows.forEach((row, rowOffset) => {
      const targetCueIdx = startCueIndex + rowOffset;
      if (targetCueIdx >= snapshot.length) return;

      const targetCue = snapshot[targetCueIdx];
      const cols = row.split('\t');
      const updates = {};

      cols.forEach((rawValue, colOffset) => {
        const fieldIdx = startColIdx + colOffset;
        if (fieldIdx >= PASTE_FIELDS.length) return;
        const field = PASTE_FIELDS[fieldIdx];
        const processed = processValueForField(field, rawValue);
        if (processed !== undefined) updates[field] = processed;
      });

      if (!canProduce) {
        for (const field of Object.keys(updates)) {
          if (!TEXT_FIELDS.includes(field)) delete updates[field];
        }
      }
      if (Object.keys(updates).length === 0) return;

      updateCue({ id: targetCue.id, ...updates });
      cuesAPI.update(showId, targetCue.id, updates).catch(err =>
        console.error('Paste update failed:', err.message)
      );
    });
  }

  // ── Selection ────────────────────────────────────────────────────────────────
  function handleSelect(cueId, checked) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(cueId); else next.delete(cueId);
      return next;
    });
  }

  function handleSelectAll(checked) {
    setSelectedIds(checked ? new Set(cues.map(c => c.id)) : new Set());
  }

  // ── Delete ───────────────────────────────────────────────────────────────────
  async function handleDelete(cueId) {
    if (!canProduce) return;
    removeCue(cueId);
    try {
      await cuesAPI.delete(showId, cueId);
    } catch (err) {
      console.error('Delete failed:', err.message);
    }
  }

  async function handleBulkDelete() {
    if (!canProduce || selectedIds.size === 0) return;
    if (!confirm(`Delete ${selectedIds.size} cue${selectedIds.size > 1 ? 's' : ''}?`)) return;
    const ids = [...selectedIds];
    ids.forEach(id => removeCue(id));
    setSelectedIds(new Set());
    await Promise.all(ids.map(id => cuesAPI.delete(showId, id).catch(console.error)));
  }

  // ── Add row ──────────────────────────────────────────────────────────────────
  async function handleAddRow() {
    if (!canProduce) return;
    try {
      const res = await cuesAPI.create(showId, {
        label: '',
        cue_type: 'SEGMENT',
        planned_duration: 60,
      });
      addCue(res.data);
    } catch (err) {
      console.error('Add row failed:', err.message);
    }
  }

  // ── DnD reorder ──────────────────────────────────────────────────────────────
  function handleDragEnd(event) {
    const { active, over } = event;
    if (!canProduce || !over || active.id === over.id) return;

    const oldIndex = cues.findIndex(c => c.id === active.id);
    const newIndex = cues.findIndex(c => c.id === over.id);
    const reordered = arrayMove(cues, oldIndex, newIndex);
    const orderedIds = reordered.map(c => c.id);

    reorderCues(orderedIds);
    cuesAPI.reorder(showId, orderedIds).catch(err => {
      console.error('Reorder failed:', err.message);
      setCues(cues);
    });
  }

  const noTimeInfo = !showStartDate;

  return (
    <div className="flex flex-col h-full bg-app-bg overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-3 py-1.5 bg-app-sub border-b border-app-border flex-shrink-0">
        <span className="text-gray-600 text-xs">{cues.length} rows</span>
        {canProduce && selectedIds.size > 0 && (
          <>
            <span className="text-blue-500 text-xs">{selectedIds.size} selected</span>
            <button
              onClick={handleBulkDelete}
              className="px-2 py-0.5 text-xs bg-red-900/40 hover:bg-red-800/60 text-red-400 rounded border border-red-900/60"
            >
              Delete selected
            </button>
          </>
        )}
        <div className="flex-1" />
        {noTimeInfo && (
          <span className="text-amber-700 text-xs">Set a scheduled start time to see Time In / Out</span>
        )}
        <span className="text-gray-700 text-xs italic">
          {canProduce
            ? 'Click a cell to edit · Paste from spreadsheet fills downward'
            : 'As a guest you can edit Script, Notes and the graphics columns'}
        </span>
        {canProduce && (
        <button
          onClick={handleAddRow}
          className="px-2 py-0.5 text-xs bg-app-surface hover:bg-app-border text-gray-400 rounded border border-app-border-strong"
        >
          + Add Row
        </button>
        )}
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={handleDragEnd}
        >
          <table className="border-collapse text-xs" style={{ tableLayout: 'fixed', minWidth: '1460px' }}>
            <colgroup>
              <col style={{ width: '52px' }} />
              <col style={{ width: '32px' }} />
              <col style={{ width: '200px' }} />
              <col style={{ width: '76px' }} />
              <col style={{ width: '76px' }} />
              <col style={{ width: '76px' }} />
              <col style={{ width: '148px' }} />
              <col style={{ width: '220px' }} />
              <col style={{ width: '140px' }} />
              <col style={{ width: '120px' }} />
              <col style={{ width: '120px' }} />
              <col style={{ width: '140px' }} />
              <col style={{ width: '32px' }} />
            </colgroup>
            <thead className="sticky top-0 z-10 bg-app-sub border-b border-app-border2">
              <tr>
                <th className="px-2 py-1.5 text-left text-gray-600 font-medium border-r border-app-border select-none">#</th>
                <th className="px-1 py-1.5 border-r border-app-border">
                  <input
                    type="checkbox"
                    checked={selectedIds.size === cues.length && cues.length > 0}
                    onChange={e => handleSelectAll(e.target.checked)}
                    className="accent-blue-500 cursor-pointer"
                  />
                </th>
                <th className="px-2 py-1.5 text-left text-gray-500 font-medium border-r border-app-border uppercase tracking-wider select-none">Label</th>
                <th className="px-2 py-1.5 text-left text-gray-500 font-medium border-r border-app-border uppercase tracking-wider select-none">Duration</th>
                <th className="px-2 py-1.5 text-left text-gray-500 font-medium border-r border-app-border uppercase tracking-wider select-none">Time In</th>
                <th className="px-2 py-1.5 text-left text-gray-500 font-medium border-r border-app-border uppercase tracking-wider select-none">Time Out</th>
                <th className="px-2 py-1.5 text-left text-gray-500 font-medium border-r border-app-border uppercase tracking-wider select-none">Type</th>
                <th className="px-2 py-1.5 text-left text-gray-500 font-medium border-r border-app-border uppercase tracking-wider select-none">Script</th>
                <th className="px-2 py-1.5 text-left text-gray-500 font-medium border-r border-app-border uppercase tracking-wider select-none">Notes</th>
                <th className="px-2 py-1.5 text-left text-gray-500 font-medium border-r border-app-border uppercase tracking-wider select-none">TR Logo</th>
                <th className="px-2 py-1.5 text-left text-gray-500 font-medium border-r border-app-border uppercase tracking-wider select-none">BR Logo</th>
                <th className="px-2 py-1.5 text-left text-gray-500 font-medium border-r border-app-border uppercase tracking-wider select-none">Other GFX</th>
                <th />
              </tr>
            </thead>
            <tbody>
              <SortableContext items={cues.map(c => c.id)} strategy={verticalListSortingStrategy}>
                {cues.map((cue, idx) => {
                  const timeIn = inTimes[idx] || null;
                  const timeOut = timeIn
                    ? new Date(timeIn.getTime() + (cue.planned_duration || 0) * 1000)
                    : null;
                  return (
                    <SortableRow
                      key={cue.id}
                      cue={cue}
                      cueIndex={idx}
                      showId={showId}
                      selected={selectedIds.has(cue.id)}
                      onSelect={handleSelect}
                      focusedCell={focusedCell}
                      onCellFocus={(cueId, field) => setFocusedCell({ cueId, field })}
                      onCellBlur={() => setFocusedCell(null)}
                      onCellChange={handleCellChange}
                      onPaste={handlePaste}
                      onDelete={handleDelete}
                      timeIn={timeIn}
                      timeOut={timeOut}
                    />
                  );
                })}
              </SortableContext>
            </tbody>
          </table>

          {cues.length === 0 && (
            <div className="text-center py-16 text-gray-700">
              No cues yet.{' '}
              <button onClick={handleAddRow} className="text-gray-500 hover:text-gray-300 underline">
                Add a row
              </button>
            </div>
          )}
        </DndContext>
      </div>
    </div>
  );
}
